/**
 * video-section-2.js
 * Handles all [data-vs2] section instances on the page.
 * Responsibilities:
 *   click_to_play: inject live player on play-button/poster click.
 *   modal:          open/close accessible modal with video.
 *   autoplay_muted_loop: IntersectionObserver pause/resume + reduced-motion.
 * No external dependencies. Loaded with defer.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Build external iframe src ── */
  function buildExtSrc(extType, extId, autoplay) {
    var auto = autoplay ? 1 : 0;
    if (extType === 'youtube') {
      return 'https://www.youtube.com/embed/' + extId
        + '?autoplay=' + auto + '&rel=0&playsinline=1';
    }
    if (extType === 'vimeo') {
      return 'https://player.vimeo.com/video/' + extId
        + '?autoplay=' + auto;
    }
    return '';
  }

  /* ── Build native video element ── */
  function buildNativeVideo(sources, autoplay) {
    var video = document.createElement('video');
    video.className = 'vs2__video';
    video.controls = true;
    video.playsinline = true;
    video.preload = 'auto';
    if (autoplay) { video.autoplay = true; }
    sources.forEach(function (s) {
      var src = document.createElement('source');
      src.src = s.url;
      src.type = s.mime;
      video.appendChild(src);
    });
    return video;
  }

  /* ── Build iframe element ── */
  function buildIframe(src, title) {
    var iframe = document.createElement('iframe');
    iframe.className = 'vs2__iframe';
    iframe.src = src;
    iframe.title = title || 'Video';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.loading = 'lazy';
    return iframe;
  }

  /* ── Modal ── */
  function initModal(sectionEl, config, title) {
    var playBtns  = sectionEl.querySelectorAll('[data-vs2-play]');
    var poster    = sectionEl.querySelector('[data-vs2-poster]');
    var modal     = sectionEl.querySelector('[data-vs2-modal]');
    var modalVideo = modal && modal.querySelector('[data-vs2-modal-video]');
    if (!modal || !modalVideo) return;

    var triggerEl = null;

    function openModal() {
      var player;
      if (config.nativeSources && config.nativeSources.length) {
        player = buildNativeVideo(config.nativeSources, true);
      } else if (config.extType && config.extId) {
        var src = buildExtSrc(config.extType, config.extId, true);
        player = buildIframe(src, title);
      }
      if (!player) return;

      modalVideo.innerHTML = '';
      modalVideo.appendChild(player);

      if (player.tagName === 'VIDEO') {
        player.play().catch(function () {});
      }

      modal.removeAttribute('hidden');
      modal.classList.add('vs2__modal--open');
      document.body.style.overflow = 'hidden';

      /* Focus first focusable */
      var focusable = getFocusable(modal);
      if (focusable[0]) focusable[0].focus();
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      modal.classList.remove('vs2__modal--open');
      modalVideo.innerHTML = '';
      document.body.style.overflow = '';
      if (triggerEl) triggerEl.focus();
    }

    /* Trap focus */
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab') return;
      var focusable = getFocusable(modal);
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });

    /* Close buttons + backdrop */
    modal.querySelectorAll('[data-vs2-modal-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    /* Trigger play */
    var triggers = [];
    playBtns.forEach(function (btn) { triggers.push(btn); });
    if (config.wholeClickable && poster) { triggers.push(poster); }

    triggers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        /* Don't fire if a child button was clicked (avoid double-trigger) */
        if (el === poster && e.target.closest('[data-vs2-play]')) return;
        triggerEl = el.tagName === 'BUTTON' ? el : sectionEl.querySelector('[data-vs2-play]');
        openModal();
      });
    });
  }

  /* ── Click to play (inline replacement) ── */
  function initClickToPlay(sectionEl, config, title) {
    var playBtns = sectionEl.querySelectorAll('[data-vs2-play]');
    var poster   = sectionEl.querySelector('[data-vs2-poster]');
    var media    = sectionEl.querySelector('[data-vs2-media]');

    function activate(e) {
      if (poster && e.target && e.target.closest('[data-vs2-play]') === null && !config.wholeClickable) return;

      var player;
      if (config.nativeSources && config.nativeSources.length) {
        player = buildNativeVideo(config.nativeSources, true);
      } else if (config.extType && config.extId) {
        var src = buildExtSrc(config.extType, config.extId, true);
        player = buildIframe(src, title);
      }
      if (!player || !media) return;

      /* Remove poster */
      if (poster) poster.remove();

      /* Insert player */
      player.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
      if (player.tagName === 'VIDEO') {
        player.className = 'vs2__video';
        player.controls = true;
      }
      media.appendChild(player);

      if (player.tagName === 'VIDEO') {
        player.play().catch(function () {});
      }
    }

    playBtns.forEach(function (btn) {
      btn.addEventListener('click', activate);
    });

    if (config.wholeClickable && poster) {
      poster.addEventListener('click', function (e) {
        /* Delegate — play btn inside poster also fires this; prevent double call */
        if (e.target.closest('[data-vs2-play]')) return;
        activate(e);
      });
    }
  }

  /* ── Autoplay muted loop: IntersectionObserver + reduced-motion ── */
  function initAutoplayLoop(sectionEl) {
    var video = sectionEl.querySelector('[data-vs2-video]');
    if (!video) return;

    /* Reduced motion: pause and show a play button */
    if (prefersReducedMotion) {
      video.pause();
      /* Show poster + play button by swapping mode class */
      sectionEl.classList.remove('vs2--mode-autoplay_muted_loop');
      sectionEl.classList.add('vs2--mode-click_to_play');
      /* Rebuild play flow */
      var config = readConfig(sectionEl);
      if (config) initClickToPlay(sectionEl, config, '');
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.1 });

    observer.observe(sectionEl);
  }

  /* ── Helpers ── */
  function getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ));
  }

  function readConfig(sectionEl) {
    var cfgEl = sectionEl.querySelector('[data-vs2-config]');
    if (!cfgEl) return null;
    try { return JSON.parse(cfgEl.textContent); } catch (e) { return null; }
  }

  /* ── Init all section instances ── */
  function init() {
    document.querySelectorAll('[data-vs2]').forEach(function (sectionEl) {
      var mode  = sectionEl.dataset.mode;
      var config = readConfig(sectionEl);
      if (!config) return;

      var titleEl = sectionEl.querySelector('.vs2__heading');
      var title   = titleEl ? titleEl.textContent.trim() : '';

      if (mode === 'click_to_play') {
        initClickToPlay(sectionEl, config, title);
      } else if (mode === 'modal') {
        initModal(sectionEl, config, title);
      } else if (mode === 'autoplay_muted_loop') {
        initAutoplayLoop(sectionEl);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
